/**
 * Scroll reveal
 * ----------------------------------------------------------------------------
 * Arms sections for the reveal defined in assets/pebble-reveal.css and fires
 * each one as it enters the viewport.
 *
 * Three decisions worth knowing about, because each one is a place this kind of
 * effect usually goes wrong:
 *
 * 1. MARKETING SURFACES ONLY.
 *    Runs on the home page and content pages. Not on collection, product, cart
 *    or search. A shopper scrolls a product grid over and over while deciding,
 *    and motion they have to sit through on every pass turns from atmosphere
 *    into a toll. Storytelling motion belongs where the story is being told.
 *
 * 2. THE FIRST SECTION IS NEVER ARMED.
 *    The largest element above the fold is what Core Web Vitals measures as
 *    Largest Contentful Paint, and Shopify reports that score back to the
 *    merchant. Fading the hero in means deliberately posting a slower number.
 *    The first section keeps whatever entrance its own component gives it.
 *
 * 3. IT FIRES ONCE.
 *    Each section is unobserved the moment it reveals. Re-animating on every
 *    scroll past is an interface arguing with its reader.
 *
 * Failure direction is always toward visible content. Anything unexpected and
 * the `.reveal-on` flag comes off, which makes every CSS rule inert.
 */

(() => {
  const root = document.documentElement;

  // The inline head script did not run, so nothing is hidden. Nothing to do.
  if (!root.classList.contains('reveal-on')) return;

  /** Undo the flag, leaving all content visible. The safe exit from anywhere. */
  const standDown = () => root.classList.remove('reveal-on');

  if (!('IntersectionObserver' in window)) return standDown();

  const start = () => {
    const main = document.getElementById('MainContent');
    if (!main) return standDown();

    // `data-template` is written by layout/theme.liquid. Values look like
    // "index", "product", "collection", "page.about".
    const template = main.dataset.template || '';
    if (!/^(index|page)\b/.test(template)) return standDown();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.setAttribute('data-revealed', '');
          observer.unobserve(entry.target);
        }
      },
      {
        // Hold the reveal until the element is a little way into view, so it
        // plays where the reader is looking rather than at the screen edge.
        rootMargin: '0px 0px -12% 0px',
        threshold: 0,
      }
    );

    /**
     * Arm one element: hide it, then watch for it.
     * A zero-height element is skipped. There is nothing to reveal, and an
     * element with no box may never report as intersecting, which would leave
     * it hidden for good.
     */
    const arm = (el) => {
      if (el.hasAttribute('data-reveal') || el.hasAttribute('data-revealed')) return;
      if (el.dataset.revealOff !== undefined) return;
      if (el.offsetHeight === 0) return;

      el.setAttribute('data-reveal', '');
      observer.observe(el);
    };

    /** Groups are authored in Liquid, so they are already marked. Just watch. */
    const armGroups = (scope) => {
      scope.querySelectorAll('[data-reveal-group]').forEach((group) => {
        if (group.hasAttribute('data-revealed')) return;
        observer.observe(group);
      });
    };

    const sections = Array.from(main.querySelectorAll('.shopify-section'));
    sections.forEach((section, index) => {
      if (index === 0) return; // see decision 2
      arm(section);
    });
    armGroups(main);

    /**
     * Theme editor. Adding or editing a section re-renders it, which wipes both
     * attributes and hands back a fresh element. Re-arm it so the merchant sees
     * the same motion a shopper will, rather than a section that never appears.
     */
    document.addEventListener('shopify:section:load', (event) => {
      const section = event.target;
      if (!(section instanceof HTMLElement) || !main.contains(section)) return;
      if (section === sections[0]) return;

      arm(section);
      armGroups(section);
    });
  };

  // `offsetHeight` is read during arming, so wait until there is a layout to
  // read. On a parsed-ahead document this runs on the next tick.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
